'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

interface SignUpFormValues {
  nomeCompleto: string;
  email: string;
  matricula: string;
  perfil: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const perfis = [
  { value: 'gestor',      label: 'Gestor de Manutenção' },
  { value: 'tecnico',     label: 'Técnico de Campo' },
  { value: 'solicitante', label: 'Solicitante (Docente/Servidor)' },
  { value: 'diretor',     label: 'Diretor / Gestão Superior' },
];

export default function SignUpForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    defaultValues: {
      nomeCompleto: '',
      email: '',
      matricula: '',
      perfil: '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
  });

  const passwordValue = watch('password');

  // Backend integration point: replace with POST /api/auth/register
  const onSubmit = async (data: SignUpFormValues) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);
    toast.success('Conta criada! Aguarde aprovação do administrador para ativar o acesso.');
  };

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground">Criar conta</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Preencha os dados para solicitar acesso ao sistema
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Nome completo */}
        <div>
          <label htmlFor="signup-nome" className="block text-sm font-600 text-foreground mb-1.5">
            Nome completo
          </label>
          <input
            id="signup-nome"
            type="text"
            autoComplete="name"
            placeholder="Ex.: Rafael Costa Pereira"
            {...register('nomeCompleto', {
              required: 'Nome completo é obrigatório.',
              minLength: { value: 5, message: 'Informe o nome completo.' },
            })}
            className={`w-full px-3.5 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
              errors.nomeCompleto ? 'border-red-400 bg-red-50/30' : 'border-border'
            }`}
          />
          {errors.nomeCompleto && (
            <p className="mt-1.5 text-xs text-red-600 font-500">{errors.nomeCompleto.message}</p>
          )}
        </div>

        {/* E-mail */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-600 text-foreground mb-1.5">
            E-mail institucional
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="seu.nome@campus.edu.br"
            {...register('email', {
              required: 'E-mail é obrigatório.',
              pattern: {
                value: /^[^\s@]+@campus\.edu\.br$/,
                message: 'Use seu e-mail institucional (@campus.edu.br).',
              },
            })}
            className={`w-full px-3.5 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
              errors.email ? 'border-red-400 bg-red-50/30' : 'border-border'
            }`}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Somente e-mails @campus.edu.br são aceitos.
          </p>
          {errors.email && (
            <p className="mt-1 text-xs text-red-600 font-500">{errors.email.message}</p>
          )}
        </div>

        {/* Matrícula + Perfil — side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="signup-matricula" className="block text-sm font-600 text-foreground mb-1.5">
              Matrícula
            </label>
            <input
              id="signup-matricula"
              type="text"
              placeholder="Ex.: 20240312"
              {...register('matricula', {
                required: 'Matrícula obrigatória.',
                pattern: { value: /^\d{6,10}$/, message: 'Somente números (6–10 dígitos).' },
              })}
              className={`w-full px-3.5 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
                errors.matricula ? 'border-red-400 bg-red-50/30' : 'border-border'
              }`}
            />
            {errors.matricula && (
              <p className="mt-1.5 text-xs text-red-600 font-500">{errors.matricula.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="signup-perfil" className="block text-sm font-600 text-foreground mb-1.5">
              Perfil de acesso
            </label>
            <select
              id="signup-perfil"
              {...register('perfil', { required: 'Selecione um perfil.' })}
              className={`w-full px-3.5 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring text-foreground transition-colors duration-150 ${
                errors.perfil ? 'border-red-400 bg-red-50/30' : 'border-border'
              }`}
            >
              <option value="">Selecionar...</option>
              {perfis.map((p) => (
                <option key={`perfil-${p.value}`} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            {errors.perfil && (
              <p className="mt-1.5 text-xs text-red-600 font-500">{errors.perfil.message}</p>
            )}
          </div>
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-600 text-foreground mb-1.5">
            Senha
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">
            Mínimo 8 caracteres, com letra maiúscula, número e símbolo.
          </p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Crie uma senha segura"
              {...register('password', {
                required: 'Senha é obrigatória.',
                minLength: { value: 8, message: 'Mínimo 8 caracteres.' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
                  message: 'Inclua maiúscula, número e símbolo.',
                },
              })}
              className={`w-full px-3.5 py-2.5 pr-10 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
                errors.password ? 'border-red-400 bg-red-50/30' : 'border-border'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600 font-500">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-600 text-foreground mb-1.5">
            Confirmar senha
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repita a senha"
              {...register('confirmPassword', {
                required: 'Confirmação obrigatória.',
                validate: (val) =>
                  val === passwordValue || 'As senhas não coincidem.',
              })}
              className={`w-full px-3.5 py-2.5 pr-10 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring placeholder:text-muted-foreground transition-colors duration-150 ${
                errors.confirmPassword ? 'border-red-400 bg-red-50/30' : 'border-border'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showConfirm ? 'Ocultar confirmação' : 'Mostrar confirmação'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1.5 text-xs text-red-600 font-500">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2">
          <input
            id="signup-terms"
            type="checkbox"
            {...register('terms', {
              required: 'Aceite os termos para continuar.',
            })}
            className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-ring/30 cursor-pointer shrink-0"
          />
          <label htmlFor="signup-terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
            Concordo com os{' '}
            <span className="text-primary hover:underline cursor-pointer font-500">Termos de Uso</span>{' '}
            e a{' '}
            <span className="text-primary hover:underline cursor-pointer font-500">Política de Privacidade</span>{' '}
            da plataforma CampusMaint.
          </label>
        </div>
        {errors.terms && (
          <p className="text-xs text-red-600 font-500 -mt-2">{errors.terms.message}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground text-sm font-700 py-2.5 rounded-lg hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 shadow-card mt-1"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Criando conta...
            </>
          ) : (
            <>
              <UserPlus size={16} />
              Solicitar acesso
            </>
          )}
        </button>

        <p className="text-xs text-muted-foreground text-center">
          Sua conta será ativada após aprovação do administrador do sistema.
        </p>
      </form>
    </div>
  );
}